import React, { useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Building, Users, User, Handshake } from 'lucide-react';

const JurisdictionSection = ({ onOpenModal }) => {
  const [activeTab, setActiveTab] = useState(0);

  const jurisdictions = [
    {
      icon: Building,
      title: 'Mainland',
      subtitle: 'Company Setup',
      shortDesc: 'Set up your company in Mainland and enjoy your freedom to conduct business anywhere in the UAE.',
      fullDesc: 'Get access to a wide range of investment opportunities, and benefit from the Emirate\'s favorable business environment. Mainland company setup enjoys a broad scope of trade and business activities within and outside the UAE.',
      link: '#mainland',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: Users,
      title: 'Free Zone',
      subtitle: 'Company Setup',
      shortDesc: 'For every business activity, there\'s a right jurisdiction. Choose from over 50 free zones in the UAE.',
      fullDesc: 'Discover the perfect jurisdiction for your business activity from a diverse range of over 50 free zones in the UAE. For multinational companies, free zone setups offer significant advantages. Enjoy full foreign company ownership and benefit from attractive low tax rates.',
      link: '#freezone',
      color: 'from-purple-500 to-purple-600',
    },
    {
      icon: User,
      title: 'Offshore',
      subtitle: 'Company Setup',
      shortDesc: 'Conduct business activities without the need for any physical presence in the UAE.',
      fullDesc: 'Just like in Mainland and Free Zone, enjoy a favorable business ecosystem in a more convenient setup. Offshore companies also get access to the UAE\'s wide range of services, such as trade finance, business consultancy, and legal advice.',
      link: '#offshore',
      color: 'from-green-500 to-green-600',
    },
    {
      icon: Handshake,
      title: 'Saudi Arabia',
      subtitle: 'KSA Setup',
      shortDesc: 'Expand your business to Saudi Arabia with our expert guidance and support.',
      fullDesc: 'Saudi Arabia offers tremendous opportunities with Vision 2030 reforms. We help you navigate the KSA business setup process, from company registration to commercial registration and sponsorship arrangements.',
      link: '#ksa',
      color: 'from-orange-500 to-orange-600',
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Choose Your Business Structure
          </h2>
          <p className="text-xl text-gray-600">Select the right setup for your entrepreneurial journey</p>
        </div>

        {/* Tab Headers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {jurisdictions.map((jurisdiction, idx) => {
            const IconComponent = jurisdiction.icon;
            return (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`p-6 rounded-xl transition-all duration-300 ${
                  activeTab === idx
                    ? `bg-gradient-to-br ${jurisdiction.color} text-white shadow-xl scale-105`
                    : 'bg-white text-gray-700 hover:shadow-lg border border-gray-200'
                }`}
              >
                <IconComponent className="mx-auto mb-3" size={32} />
                <h3 className="font-bold text-lg">{jurisdiction.title}</h3>
                <p className="text-sm opacity-90">{jurisdiction.subtitle}</p>
              </button>
            );
          })}
        </div>

        {/* Active Tab Content */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Image Side */}
            <div 
              className="h-64 md:h-auto bg-cover bg-center"
              style={{
                backgroundImage: `linear-gradient(rgba(107, 27, 77, 0.7), rgba(107, 27, 77, 0.7)), url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80)`,
              }}
            >
              <div className="h-full flex items-center justify-center p-8">
                {React.createElement(jurisdictions[activeTab].icon, {
                  size: 120,
                  className: 'text-white opacity-50',
                })}
              </div>
            </div>

            {/* Content Side */}
            <div className="p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                {React.createElement(jurisdictions[activeTab].icon, {
                  size: 48,
                  className: 'text-[#6B1B4D]',
                })}
                <div>
                  <h3 className="text-3xl font-bold text-gray-800">
                    {jurisdictions[activeTab].title}
                  </h3>
                  <p className="text-xl text-gray-600">{jurisdictions[activeTab].subtitle}</p>
                </div>
              </div>

              <p className="text-gray-700 text-lg mb-4 leading-relaxed">
                {jurisdictions[activeTab].shortDesc}
              </p>

              <p className="text-gray-600 mb-8 leading-relaxed">
                {jurisdictions[activeTab].fullDesc}
              </p>

              <Button 
                className={`bg-gradient-to-r ${jurisdictions[activeTab].color} text-white hover:shadow-lg transition-all group`}
                size="lg"
                onClick={onOpenModal}
              >
                Learn More
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JurisdictionSection;